import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Swords, RefreshCw, ExternalLink, Tag, Gift, Sparkles, Filter, TrendingDown, TrendingUp, Activity } from 'lucide-react';
import { Line, LineChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot, Legend } from 'recharts';
import { Navigation } from '../components/Navigation';
import api from '../utils/api';

const typeColors = {
  rabat: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  service: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
  blandet: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
};

const typeIcons = {
  rabat: Tag,
  service: Gift,
  blandet: Sparkles,
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'netop nu';
    if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} timer siden`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} dage siden`;
    return formatDate(iso);
  } catch {
    return formatDate(iso);
  }
};

const CompetitorCard = ({ c, onScan, scanning }) => {
  const TypeIcon = c.type ? typeIcons[c.type] : Tag;
  const isActive = c.has_active_campaign;
  return (
    <div
      data-testid={`competitor-card-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
      className={`bg-[#12131a] border rounded-lg p-5 flex flex-col gap-3 transition-colors ${isActive ? 'border-emerald-500/30' : 'border-white/10'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-white font-semibold text-base">{c.name}</h3>
          <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-[#1E6F7A] hover:underline inline-flex items-center gap-1">
            {c.url.replace(/^https?:\/\//, '')}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-widest font-semibold ${isActive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/5 text-white/40'}`}>
          {c.status}
        </span>
      </div>

      <div className="flex-1">
        {c.campaign_headline ? (
          <>
            <div className="text-sm text-white/90 font-medium leading-snug line-clamp-2 mb-1">{c.campaign_headline}</div>
            {c.summary_da && <div className="text-xs text-white/50 leading-relaxed line-clamp-3">{c.summary_da}</div>}
          </>
        ) : (
          <div className="text-xs text-white/30 italic">Ingen aktiv kampagne detekteret</div>
        )}
      </div>

      <div className="flex items-center flex-wrap gap-2 pt-1">
        {typeof c.discount_percent === 'number' && c.discount_percent > 0 && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-400 font-semibold">
            −{c.discount_percent}%
          </span>
        )}
        {c.type && (
          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${typeColors[c.type] || 'border-white/10 text-white/50'}`}>
            <TypeIcon className="w-3 h-3" />
            {c.type}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <div className="text-[10px] text-white/30">
          Sidst scannet: <span className="text-white/50">{formatRelative(c.last_scanned_at)}</span>
        </div>
        <button
          data-testid={`scan-button-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
          onClick={() => onScan(c.name)}
          disabled={scanning}
          className="text-xs px-3 py-1 rounded bg-white/5 hover:bg-[#1E6F7A]/30 text-white/70 hover:text-white transition-colors inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          <RefreshCw className={`w-3 h-3 ${scanning ? 'animate-spin' : ''}`} />
          Scan
        </button>
      </div>
    </div>
  );
};

const ChangeFeed = ({ changes }) => {
  const badge = (type) => {
    switch (type) {
      case 'new_campaign':
        return <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-semibold">Ny</span>;
      case 'changed_campaign':
        return <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-semibold">Ændret</span>;
      case 'baseline':
        return <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 font-semibold">Baseline</span>;
      default:
        return <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/5 text-white/40 font-semibold">Ingen ændring</span>;
    }
  };

  if (!changes || changes.length === 0) {
    return <div className="text-white/30 text-sm italic py-6 text-center">Ingen ændringer registreret endnu. Tryk "Scan alle" for at starte overvågningen.</div>;
  }

  return (
    <ul className="divide-y divide-white/5">
      {changes.map((ch) => (
        <li key={ch.id} className="flex items-start gap-3 py-3" data-testid={`change-${ch.id}`}>
          <div className="pt-0.5 w-24 shrink-0">{badge(ch.change_type)}</div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">{ch.competitor_name}</div>
            <div className="text-white/60 text-xs mt-0.5 leading-relaxed">{ch.short_description}</div>
          </div>
          <div className="text-[10px] text-white/30 shrink-0 pt-1">{formatRelative(ch.created_at)}</div>
        </li>
      ))}
    </ul>
  );
};

const fmtPct = (n) => {
  if (n === null || n === undefined) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};

const pctColor = (n, inverse = false) => {
  if (n === null || n === undefined) return 'text-white/40';
  const neg = n < -2;
  const pos = n > 2;
  if (inverse) {
    // For PPB (price per booking), increase is bad
    if (pos) return 'text-rose-400';
    if (neg) return 'text-emerald-400';
    return 'text-white/60';
  }
  if (pos) return 'text-emerald-400';
  if (neg) return 'text-rose-400';
  return 'text-white/60';
};

const EffectChart = ({ daily, dataKey, label, unit }) => {
  const chartData = daily.map((d) => ({
    date: d.date.slice(5),
    value: d[dataKey],
    hasCampaign: d.active_count > 0,
    activeCount: d.active_count,
    activeCompetitors: d.active_competitors || [],
  }));
  return (
    <div className="h-[220px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
          <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
          <Tooltip
            contentStyle={{ backgroundColor: '#0B0C10', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, fontSize: 11 }}
            labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
            formatter={(v, _n, p) => [`${v ?? '—'} ${unit}`, label]}
            labelFormatter={(l, payload) => {
              const item = payload && payload[0] && payload[0].payload;
              if (item && item.hasCampaign) {
                return `${l} — ${item.activeCount} aktiv(e) konkurrent(er): ${item.activeCompetitors.join(', ')}`;
              }
              return `${l}`;
            }}
          />
          <Line type="monotone" dataKey="value" name={label} stroke="#1E6F7A" strokeWidth={2} dot={(props) => {
            const { cx, cy, payload } = props;
            if (payload && payload.hasCampaign) {
              return <circle cx={cx} cy={cy} r={4} fill="#f43f5e" stroke="#0B0C10" strokeWidth={1.5} />;
            }
            return <circle cx={cx} cy={cy} r={2.5} fill="#1E6F7A" />;
          }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const EffectSection = ({ days, onDaysChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/competitors/effect?days=${days}`);
        setData(res.data);
      } catch (e) {
        console.error('Load effect error', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [days]);

  if (loading) {
    return (
      <section className="bg-[#12131a] border border-white/10 rounded-lg p-5">
        <div className="flex items-center justify-center py-10">
          <div className="w-5 h-5 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }
  if (!data) return null;

  const o = data.overall || {};
  const multi = data.multi_competitor || [];
  const perComp = data.per_competitor || [];

  return (
    <section className="bg-[#12131a] border border-white/10 rounded-lg p-5 space-y-6" data-testid="effect-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#1E6F7A]" />
          <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-semibold">Effekt på vores forretning</h2>
        </div>
        <div className="flex items-center gap-2" data-testid="effect-days-filter">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`effect-days-${d}`}
              onClick={() => onDaysChange(d)}
              className={`text-xs px-3 py-1 rounded transition-colors ${days === d ? 'bg-[#1E6F7A] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
            >
              Sidste {d} dage
            </button>
          ))}
        </div>
      </div>

      {/* Overall metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white/[0.02] border border-white/5 rounded p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Bookinger</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white/50 text-xs">Ingen konkurrent-kampagne</div>
              <div className="text-white text-lg font-semibold tabular-nums">{o.avg_bookings_no_campaign ?? '—'}</div>
              <div className="text-[10px] text-white/30">{o.days_no_campaign} dage</div>
            </div>
            <div className="text-right">
              <div className="text-white/50 text-xs">Mindst én aktiv kampagne</div>
              <div className="text-white text-lg font-semibold tabular-nums">{o.avg_bookings_with_campaign ?? '—'}</div>
              <div className="text-[10px] text-white/30">{o.days_with_campaign} dage</div>
            </div>
          </div>
          <div className={`mt-2 text-sm font-semibold flex items-center gap-1 ${pctColor(o.bookings_change_pct, false)}`}>
            {o.bookings_change_pct === null ? null : o.bookings_change_pct < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
            {fmtPct(o.bookings_change_pct)} ændring
          </div>
        </div>

        <div className="bg-white/[0.02] border border-white/5 rounded p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Pris pr. booking</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white/50 text-xs">Ingen konkurrent-kampagne</div>
              <div className="text-white text-lg font-semibold tabular-nums">{o.avg_ppb_no_campaign ? `${o.avg_ppb_no_campaign.toLocaleString('da-DK')} kr` : '—'}</div>
            </div>
            <div className="text-right">
              <div className="text-white/50 text-xs">Mindst én aktiv kampagne</div>
              <div className="text-white text-lg font-semibold tabular-nums">{o.avg_ppb_with_campaign ? `${o.avg_ppb_with_campaign.toLocaleString('da-DK')} kr` : '—'}</div>
            </div>
          </div>
          <div className={`mt-2 text-sm font-semibold flex items-center gap-1 ${pctColor(o.ppb_change_pct, true)}`}>
            {o.ppb_change_pct === null ? null : o.ppb_change_pct > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {fmtPct(o.ppb_change_pct)} ændring
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2 flex items-center justify-between">
            <span>Bookinger over tid</span>
            <span className="text-white/30 normal-case tracking-normal text-[10px]"><span className="inline-block w-2 h-2 bg-rose-400 rounded-full mr-1" />Dage med aktiv konkurrent-kampagne</span>
          </div>
          <EffectChart daily={data.daily} dataKey="bookings" label="Bookinger" unit="" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2 flex items-center justify-between">
            <span>Pris pr. booking over tid</span>
            <span className="text-white/30 normal-case tracking-normal text-[10px]"><span className="inline-block w-2 h-2 bg-rose-400 rounded-full mr-1" />Dage med aktiv konkurrent-kampagne</span>
          </div>
          <EffectChart daily={data.daily} dataKey="price_per_booking" label="Pris/booking" unit="kr" />
        </div>
      </div>

      {/* Per-competitor comparison table */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Sammenligning pr. konkurrent</div>
        <table className="w-full text-sm" data-testid="effect-per-competitor-table">
          <thead>
            <tr className="border-b border-white/10 text-[10px] text-white/40 uppercase tracking-widest">
              <th className="text-left py-2 pr-2 font-medium">Konkurrent</th>
              <th className="text-right py-2 pr-2 font-medium">Dage aktiv</th>
              <th className="text-right py-2 pr-2 font-medium">Ændring i bookinger</th>
              <th className="text-right py-2 font-medium">Ændring i pris pr. booking</th>
            </tr>
          </thead>
          <tbody>
            {perComp.map((pc) => (
              <tr key={pc.competitor} className="border-b border-white/[0.04]">
                <td className="py-2 pr-2 text-white/90 font-medium">{pc.competitor}</td>
                <td className="py-2 pr-2 text-right text-white/60 tabular-nums">{pc.days_active}</td>
                <td className={`py-2 pr-2 text-right font-semibold tabular-nums ${pctColor(pc.bookings_change_pct, false)}`}>{fmtPct(pc.bookings_change_pct)}</td>
                <td className={`py-2 text-right font-semibold tabular-nums ${pctColor(pc.ppb_change_pct, true)}`}>{fmtPct(pc.ppb_change_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Multi-competitor comparison */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Flere samtidige kampagner</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {multi.map((m, idx) => (
            <div key={idx} className="bg-white/[0.02] border border-white/5 rounded p-3">
              <div className="text-white/70 text-xs font-medium">{m.label}</div>
              <div className="text-[10px] text-white/30">{m.days} dage</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] text-white/40">Bookinger</div>
                  <div className="text-white font-semibold tabular-nums">{m.avg_bookings ?? '—'}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-white/40">Pris/booking</div>
                  <div className="text-white font-semibold tabular-nums">{m.avg_ppb ? `${m.avg_ppb.toLocaleString('da-DK')} kr` : '—'}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insights */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Indsigt</div>
        <ul className="space-y-2" data-testid="effect-insights">
          {data.insights.map((t, i) => (
            <li key={i} className="flex gap-2 items-start text-sm text-white/80 bg-white/[0.02] border-l-2 border-[#1E6F7A]/50 px-3 py-2 rounded-r">
              <span className="text-[#1E6F7A] mt-0.5">→</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const HistoryTable = ({ campaigns }) => {
  if (!campaigns || campaigns.length === 0) {
    return <div className="text-white/30 text-sm italic py-6 text-center">Ingen kampagner matcher filtrene.</div>;
  }
  return (
    <table className="w-full text-sm" data-testid="history-table">
      <thead>
        <tr className="border-b border-white/10 text-[10px] text-white/40 uppercase tracking-widest">
          <th className="text-left py-2 pr-2 font-medium">Konkurrent</th>
          <th className="text-left py-2 pr-2 font-medium">Kampagne</th>
          <th className="text-left py-2 pr-2 font-medium">Type</th>
          <th className="text-right py-2 pr-2 font-medium">Rabat</th>
          <th className="text-right py-2 pr-2 font-medium">Status</th>
          <th className="text-right py-2 font-medium">Detekteret</th>
        </tr>
      </thead>
      <tbody>
        {campaigns.map((c) => (
          <tr key={c.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`history-row-${c.id}`}>
            <td className="py-2 pr-2 text-white/90 font-medium">{c.competitor_name}</td>
            <td className="py-2 pr-2 text-white/70 max-w-md">
              <div className="truncate">{c.campaign_headline || '—'}</div>
              {c.summary_da && <div className="text-[11px] text-white/40 truncate">{c.summary_da}</div>}
            </td>
            <td className="py-2 pr-2">
              {c.type ? (
                <span className={`text-[11px] px-2 py-0.5 rounded border ${typeColors[c.type] || 'border-white/10 text-white/50'}`}>{c.type}</span>
              ) : '—'}
            </td>
            <td className="py-2 pr-2 text-right text-rose-400 tabular-nums">{typeof c.discount_percent === 'number' && c.discount_percent > 0 ? `−${c.discount_percent}%` : '—'}</td>
            <td className="py-2 pr-2 text-right">
              {c.is_active ? (
                <span className="text-[11px] text-emerald-400">Aktiv</span>
              ) : (
                <span className="text-[11px] text-white/30">Udløbet</span>
              )}
            </td>
            <td className="py-2 text-right text-white/50 text-xs tabular-nums">{formatDate(c.detected_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default function Konkurrenter() {
  const [overview, setOverview] = useState([]);
  const [changes, setChanges] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(null); // competitor name or 'all'

  const [filterCompetitor, setFilterCompetitor] = useState('Alle');
  const [filterType, setFilterType] = useState('Alle');
  const [filterActive, setFilterActive] = useState('Alle');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [effectDays, setEffectDays] = useState(30);

  const loadData = useCallback(async () => {
    try {
      const [ov, ch] = await Promise.all([
        api.get('/competitors/overview'),
        api.get('/competitors/changes?limit=30'),
      ]);
      setOverview(ov.data?.competitors || []);
      setChanges(ch.data?.changes || []);
    } catch (e) {
      console.error('Load competitors error', e);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCompetitor !== 'Alle') params.set('competitor', filterCompetitor);
      if (filterType !== 'Alle') params.set('type', filterType);
      if (filterActive === 'Aktiv') params.set('is_active', 'true');
      if (filterActive === 'Ikke aktiv') params.set('is_active', 'false');
      if (filterDateFrom) params.set('date_from', new Date(filterDateFrom).toISOString());
      if (filterDateTo) params.set('date_to', new Date(filterDateTo + 'T23:59:59').toISOString());
      const res = await api.get(`/competitors/history?${params.toString()}`);
      setCampaigns(res.data?.campaigns || []);
    } catch (e) {
      console.error('Load history error', e);
    }
  }, [filterCompetitor, filterType, filterActive, filterDateFrom, filterDateTo]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadData();
      setLoading(false);
    })();
  }, [loadData]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleScan = async (competitor) => {
    setScanning(competitor || 'all');
    try {
      const body = competitor ? { competitor } : { all: true };
      await api.post('/competitors/scan', body);
      await loadData();
      await loadHistory();
    } catch (e) {
      console.error('Scan error', e);
      alert('Kunne ikke udføre scanning. Se konsollen for detaljer.');
    } finally {
      setScanning(null);
    }
  };

  const competitorNames = useMemo(() => ['Alle', ...overview.map(c => c.name)], [overview]);

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="konkurrenter-page">
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-4">
          <Navigation />
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Swords className="w-5 h-5 text-[#1E6F7A]" />
            Konkurrenter
          </h1>
          <button
            data-testid="scan-all-button"
            onClick={() => handleScan(null)}
            disabled={scanning !== null}
            className="ml-auto text-xs px-3 py-1.5 rounded bg-[#1E6F7A] hover:bg-[#1E6F7A]/80 text-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${scanning === 'all' ? 'animate-spin' : ''}`} />
            {scanning === 'all' ? 'Scanner...' : 'Scan alle'}
          </button>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-8">
        {/* OVERSIGTSKORT */}
        <section>
          <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-semibold mb-3">Oversigt</h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="competitor-cards-grid">
              {overview.map((c) => (
                <CompetitorCard key={c.name} c={c} onScan={handleScan} scanning={scanning === c.name || scanning === 'all'} />
              ))}
            </div>
          )}
        </section>

        {/* SENESTE ÆNDRINGER */}
        <section className="bg-[#12131a] border border-white/10 rounded-lg p-5">
          <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-semibold mb-3">Seneste ændringer</h2>
          <ChangeFeed changes={changes} />
        </section>

        {/* EFFEKT PÅ VORES FORRETNING */}
        <EffectSection days={effectDays} onDaysChange={setEffectDays} />

        {/* HISTORIK */}
        <section className="bg-[#12131a] border border-white/10 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-semibold">Historik</h2>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <Filter className="w-3.5 h-3.5 text-white/30" />
              <select
                data-testid="filter-competitor"
                value={filterCompetitor}
                onChange={(e) => setFilterCompetitor(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70"
              >
                {competitorNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <select
                data-testid="filter-type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70"
              >
                {['Alle', 'rabat', 'service', 'blandet'].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <select
                data-testid="filter-active"
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70"
              >
                {['Alle', 'Aktiv', 'Ikke aktiv'].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input
                data-testid="filter-date-from"
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70"
              />
              <input
                data-testid="filter-date-to"
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70"
              />
            </div>
          </div>
          <HistoryTable campaigns={campaigns} />
        </section>
      </div>
    </div>
  );
}
